import type React from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { useNavigate } from "react-router";
import { type Message, streamChat, type VersionSnapshot } from "@/features/agent";
import { trackEvent } from "@/features/analytics";
import type { AssetFile } from "@/features/asset";
import { useAuth, useCredit } from "@/features/auth";
import { isOPFSAvailable } from "@/features/opfs";
import { useProjectList } from "./ProjectListContext";
import { ProjectManager } from "./ProjectManager";
import { ChatState } from "./types";

const useProjectValue = (currentProjectId?: string, onInsufficientCredits?: () => void) => {
  const { newProject, updateProjectListItem } = useProjectList();
  const { user, handleSessionExpired } = useAuth();
  const { fetchCredit } = useCredit();
  const navigate = useNavigate();

  const isInitialized = useRef(false);
  const [isLoading, setIsLoading] = useState(!!currentProjectId);

  const [manager, setManager] = useState(() => new ProjectManager(user.uid));
  const projectState = useSyncExternalStore(manager.subscribe, manager.getState);
  const managerRef = useRef(manager);

  const assetUrlMap = useSyncExternalStore(
    manager.assetManager.subscribe,
    manager.assetManager.getState,
  );

  const [stableSnapshot, setStableSnapshot] = useState<VersionSnapshot>({
    files: { init: "", render: "", helpers: {}, assets: [] },
    duration: 0,
    data: {},
  });

  const initManager = useCallback(
    (projectId?: string) => {
      isInitialized.current = true;
      managerRef.current.isOffscreen = true;
      managerRef.current.tryDispose();
      setIsLoading(!!projectId);
      const manager = new ProjectManager(user.uid, projectId);
      manager.init().then((success) => {
        setIsLoading(false);
        if (!success) {
          navigate("/");
          return;
        }
      });
      manager.subscribe(() => {
        if (manager.projectId) {
          const state = manager.getState();
          updateProjectListItem(manager.projectId, {
            updatedAt: Date.now(),
            messageCount: state.messages.filter((m) => m.role === "user").length,
          });
        }
      });
      setManager(manager);
      managerRef.current = manager;
      return manager;
    },
    [updateProjectListItem, user.uid, navigate],
  );

  const startManagerStream = useCallback(
    async (manager: ProjectManager, inputText: string) => {
      console.log("startManagerStream", manager.id);
      const trimmedInput = inputText.trim();
      if (trimmedInput) {
        trackEvent("chat_submit", {
          project_id: manager.projectId,
          message_length: trimmedInput.length,
        });
      }
      const { files, messages, duration, dimensions, data } = manager.getState();

      // Upload assets without URLs to cloud in background (non-blocking)
      manager.uploadAssetsToCloud();
      const userMsg: Message = {
        id: crypto.randomUUID(),
        role: "user",
        content: trimmedInput,
        timestamp: Date.now(),
        snapshot: { files, duration, data },
      };
      const responseId = crypto.randomUUID();
      const streamingMsg: Message = {
        id: responseId,
        role: "model",
        content: "",
        timestamp: Date.now(),
      };
      const newMessages = [...messages, userMsg, streamingMsg];
      manager.updateState({
        messages: newMessages,
        chatState: ChatState.THINKING,
      });

      if (!manager.projectId) {
        const project = await newProject({
          title: inputText.slice(0, 20),
          chatState: ChatState.THINKING,
          messages: newMessages,
          files,
          dimensions,
          duration,
          data,
        });
        manager.projectId = project.id;
        manager.updateState({}); // Trigger update to save to new project ID
      }

      const credits = await fetchCredit();
      const totalCost = await streamChat(
        manager.projectId,
        inputText,
        {
          history: messages,
          files,
          dimensions,
          data,
          userCredits: credits?.totalBalance ?? 0,
        },
        {
          onChunk(update) {
            manager.updateState({ chatState: ChatState.CODING });
            if (update.explanation) {
              manager.updateState({ chatState: ChatState.GENERATING });
              manager.updateMessage(responseId, { content: update.explanation });
            }
            if (update.duration) {
              manager.updateState({ duration: update.duration });
            }
            if (update.init) {
              manager.updateFiles({ init: update.init });
            }
            if (update.render) {
              manager.updateFiles({ render: update.render });
            }
            if (update.helpers) {
              // Merge incoming helpers with existing ones (AI may output one helper at a time)
              const currentHelpers = manager.getState().files.helpers || {};
              manager.updateFiles({ helpers: { ...currentHelpers, ...update.helpers } });
            }
            if (update.data) {
              manager.updateState({ data: update.data });
            }
            if (update.assets) {
              manager.pushAssets(...update.assets);
              for (const asset of update.assets) {
                manager.assetManager.set(asset.id, asset.url);
              }
            }
            if (update.dimensions) {
              manager.updateState({
                dimensions: {
                  width: update.dimensions.width,
                  height: update.dimensions.height,
                },
              });
            }
            if (update.checkpoint) {
              manager.updateState({ chatState: ChatState.GENERATING });
              manager.updateMessage(responseId, { checkpoint: update.checkpoint });
            }
          },
          onError(type, msg) {
            if (type === "UNAUTHENTICATED") {
              handleSessionExpired();
            }
            if (type === "INSUFFICIENT_BALANCE") {
              onInsufficientCredits?.();
            }
            manager.updateMessage(responseId, { error: msg });
          },
        },
      );
      trackEvent("chat_response", {
        project_id: manager.projectId,
        total_cost: totalCost,
      });
      manager.saveSnapshot(responseId);
      // Update project list with totalCost for admin users
      if (totalCost !== undefined && manager.projectId) {
        manager.updateState({ totalCost });
        updateProjectListItem(manager.projectId, { totalCost });
      }

      manager.updateState({ chatState: ChatState.IDLE });
      manager.tryDispose();
    },
    [fetchCredit, newProject, handleSessionExpired, onInsufficientCredits, updateProjectListItem],
  );

  useEffect(() => {
    if (!isInitialized.current) {
      const manager = initManager(currentProjectId);
      console.log("init manager", manager.id, "for projectId", currentProjectId);
      const params = new URLSearchParams(window.location.search);
      const initialMessage = params.get("message");
      if (initialMessage) {
        window.history.replaceState({}, "", window.location.pathname);
        startManagerStream(manager, initialMessage);
      }
    } else if (managerRef.current.projectId !== currentProjectId) {
      const lastManagerId = managerRef.current.id;
      const lastProjectId = managerRef.current.projectId;
      const manager = initManager(currentProjectId);
      console.log("switch manager", lastManagerId, "->", manager.id);
      console.log("project change", lastProjectId, "->", manager.projectId);
    }
  }, [currentProjectId, initManager, startManagerStream]);

  useEffect(() => {
    if (projectState.chatState === ChatState.IDLE) {
      setStableSnapshot({
        files: projectState.files,
        duration: projectState.duration,
        data: projectState.data,
      });
    }
  }, [projectState.files, projectState.duration, projectState.data, projectState.chatState]);

  const restoreSnapshot = useCallback(
    (snapshot: VersionSnapshot) => {
      manager.updateState({
        files: snapshot.files,
        duration: snapshot.duration,
        data: snapshot.data,
      });
      manager.assetManager.load(snapshot.files.assets);
    },
    [manager],
  );

  const uploadAssetFiles = useCallback(
    async (files: FileList) => {
      if (!files || files.length === 0) return [];

      const opfsAvailable = await isOPFSAvailable();
      if (!opfsAvailable) {
        alert("This browser does not support file storage");
        return [];
      }

      const uploadedAssets: AssetFile[] = [];
      for (const file of Array.from(files)) {
        try {
          const result = await manager.assetManager.upload(file);
          manager.pushAssets(result.asset);
          uploadedAssets.push(result.asset);
        } catch (error) {
          console.error("Asset upload failed:", error);
          if (error instanceof DOMException && error.name === "QuotaExceededError") {
            alert(
              "Storage space is insufficient. Please delete unneeded projects to free up space.",
            );
            break;
          }
        }
      }

      // Upload to cloud in background (non-blocking)
      if (uploadedAssets.length > 0) {
        manager.uploadAssetsToCloud(uploadedAssets);
      }

      return uploadedAssets;
    },
    [manager],
  );

  const submitChat = useCallback(
    (inputText: string) => startManagerStream(manager, inputText),
    [startManagerStream, manager],
  );

  const retryMessage = useCallback(
    (id: string) => {
      const { messages } = manager.getState();
      // Find the retry message index
      const retryMsgIndex = messages.findIndex((m) => m.id === id);
      if (retryMsgIndex < 1) return;

      // Find the preceding user message
      const userMsgIndex = retryMsgIndex - 1;
      const userMsg = messages[userMsgIndex];
      if (!userMsg || userMsg.role !== "user") return;

      // Remove all messages after
      const newMessages = messages.slice(0, userMsgIndex);
      manager.updateState({ messages: newMessages });

      // Restore snapshot if available, then resubmit
      if (userMsg.snapshot) {
        manager.updateState({
          files: userMsg.snapshot.files,
          duration: userMsg.snapshot.duration,
          data: userMsg.snapshot.data,
        });
        manager.assetManager.load(userMsg.snapshot.files.assets);
      }

      // Re-submit the message
      submitChat(userMsg.content);
    },
    [manager, submitChat],
  );

  return {
    ...projectState,
    isLoading,
    assetUrlMap,
    stableSnapshot,
    updateProject: manager.updateState,
    updateProjectFiles: manager.updateFiles,
    deleteAsset: manager.deleteAsset,
    renameAsset: manager.renameAsset,
    toggleTaskApproval: manager.toggleTaskApproval,
    setCheckpointApproval: manager.setCheckpointApproval,
    restoreSnapshot,
    uploadAssetFiles,
    submitChat,
    retryMessage,
  };
};

type ProjectContextValue = ReturnType<typeof useProjectValue>;

const ProjectContext = createContext<ProjectContextValue | null>(null);

export const useProject = () => {
  const ctx = useContext(ProjectContext);
  if (!ctx) throw new Error("useProject must be used within ProjectProvider");
  return ctx;
};

interface ProjectProviderProps {
  onInsufficientCredits?: () => void;
  children: React.ReactNode;
}

export const ProjectProvider: React.FC<ProjectProviderProps> = ({
  onInsufficientCredits,
  children,
}) => {
  const projectId = useProjectList().currentProjectId;
  const value = useProjectValue(projectId, onInsufficientCredits);
  return <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>;
};
